import { ResourceSchema, RootNodeSchema } from '../types/SchemaExplorer';
import { SchemaTreeNode, TreeNodeProperty } from '../types/Tree';
import { deepClone } from './Helpers';

export const convertSchemaToTree = (
  node: RootNodeSchema,
  root: boolean
): SchemaTreeNode => {
  return {
    vmid: node.vmid,
    name: node.name,
    children: [],
    root,
    state: 'collapsed',
    properties: Object.keys(node)
      .filter(key => key !== 'vmid')
      .map<TreeNodeProperty>(key => ({ name: key, value: node[key] }))
  };
};

export const convertNodeToSchema = (node: SchemaTreeNode): RootNodeSchema => {
  const schema = { vmid: node.vmid };
  node.properties.forEach(p => {
    schema[p.name] = p.value;
  });
  return schema as RootNodeSchema;
};

export const convertSchemasToTree = (
  nodes: RootNodeSchema[],
  root: boolean
): SchemaTreeNode[] => {
  return nodes.map<SchemaTreeNode>(node => convertSchemaToTree(node, root));
};

export const changeTreeState = (
  tree: SchemaTreeNode[],
  vmid: string,
  children?: SchemaTreeNode[]
): SchemaTreeNode[] => {
  const deepClonedTree = deepClone(tree);
  for (const node of deepClonedTree) {
    const updated = findAndUpdateState(node, vmid, children);
    if (updated) {
      break;
    }
  }
  return deepClonedTree;
};

const findAndUpdateState = (
  node: SchemaTreeNode,
  vmid: string,
  children?: SchemaTreeNode[]
): boolean => {
  if (node.vmid === vmid) {
    node.state = children
      ? 'expanded'
      : node.state === 'expanded'
      ? 'collapsed'
      : 'loading';
    node.children = children || [];
    return true;
  } else if (node.children) {
    let updated = false;
    for (let i = 0; !updated && i < node.children.length; i++) {
      updated = findAndUpdateState(node.children[i], vmid, children);
    }
    return updated;
  }
  return false;
};

export const addNodeToTree = (
  tree: SchemaTreeNode[],
  addedNode: ResourceSchema
): SchemaTreeNode[] => {
  const deepClonedTree = deepClone(tree);
  for (const node of deepClonedTree) {
    const updated = findAndAddChild(node, addedNode);
    if (updated) {
      break;
    }
  }
  return deepClonedTree;
};

const findAndAddChild = (
  node: SchemaTreeNode,
  addedNode: ResourceSchema
): boolean => {
  if (node.vmid === addedNode.source) {
    node.children = [...node.children, convertSchemaToTree(addedNode, false)];
    return true;
  } else if (node.children) {
    let updated = false;
    for (let i = 0; !updated && i < node.children.length; i++) {
      updated = findAndAddChild(node.children[i], addedNode);
    }
    return updated;
  }
  return false;
};

export const editNodeInTree = (
  tree: SchemaTreeNode[],
  updatedNode: ResourceSchema
): SchemaTreeNode[] => {
  const deepClonedTree = deepClone(tree);
  for (const node of deepClonedTree) {
    const updated = findAndUpdateChild(node, updatedNode);
    if (updated) {
      break;
    }
  }
  return deepClonedTree;
};

const findAndUpdateChild = (
  node: SchemaTreeNode,
  updatedNode: ResourceSchema
): boolean => {
  const updateIndex = node.children.findIndex(n => n.vmid === updatedNode.vmid);
  if (updateIndex > -1) {
    node.children[updateIndex] = convertSchemaToTree(updatedNode, false);
    return true;
  } else if (node.children) {
    let updated = false;
    for (let i = 0; !updated && i < node.children.length; i++) {
      updated = findAndUpdateChild(node.children[i], updatedNode);
    }
    return updated;
  }
  return false;
};

export const deleteNodeFromTree = (
  tree: SchemaTreeNode[],
  vmid: string
): SchemaTreeNode[] => {
  const deepClonedTree = deepClone(tree);
  for (const node of deepClonedTree) {
    const updated = findAndDeleteChild(node, vmid);
    if (updated) {
      break;
    }
  }
  return deepClonedTree;
};

const findAndDeleteChild = (node: SchemaTreeNode, vmid: string): boolean => {
  const deleteIndex = node.children.findIndex(n => n.vmid === vmid);
  if (deleteIndex > -1) {
    node.children = node.children.filter(n => n.vmid !== vmid);
    return true;
  } else if (node.children) {
    let updated = false;
    for (let i = 0; !updated && i < node.children.length; i++) {
      updated = findAndDeleteChild(node.children[i], vmid);
    }
    return updated;
  }
  return false;
};
